import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useParams } from "react-router";
import { StatusCode } from "api/protocols";
import Page from "components/page/Page";
import Card from "components/card/Card";
import FormLayout from "components/form/FormLayout";
import { isEqualWith, isNull } from "lodash";
import ReactDOM from "react-dom";

import "pages/secure/facility/product/TicketStub.scss";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import { ICustomer } from "redux/reducers/models/customer";
import { GetCustomer, PostCustomer } from "api/rpc/2024-04/facilityAdmin/customer/customer";
import { ICustomerInfoState } from "components/newCustomer/NewCustomer";
import Input from "components/form/input/Input";
import { useTranslation } from "react-i18next";
import moment from "moment";
import Markdown from "components/markdown/Markdown";

import {
  GetTicketStub,
  GetTicketStubPdf,
  PutTicketStub,
  GetTicketStubReceipt,
} from "api/rpc/2024-04/facilityAdmin/ticket/ticket";
import { customerErrorMessage, capitalize } from "helpers/Helpers";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import CustomerImage from "elements/customer/CustomerImage";
import { GetFacility } from "api/rpc/2024-04/facilityAdmin/facility/facility";
import ChangeCustomer from "components/changeCustomer/ChangeCustomer";
import { PutTicketStubRedeem, PutVoidStub, PutUnredeemStub } from "api/rpc/2024-04/facilityAdmin/ticket/stub";

interface ITicketStubState {
  id: number;
  customer_id: number;
  code: string;
  expiry_date_string: string;
  expiry_date: Date;
  last4: number;
  pin: number;
  redemption_product: string;
  source_url: string;
  description: string;
  status: string;
  selectedDate: Date;
  status_id: number;
  showDateSelector: boolean;
  customer: any;
  redeemed_facility_id: number;
  redeemed_at: any;
  facility: any;
  title: string;
  subtitle: string;
  included: string;
  logo_image: any;
  ticket_description: string;
}

interface IChangeCustomerState {
  showChangeCustomer: boolean;
  showNewCustomer: boolean;
  customerQuery: string;
  customerSearching: boolean;
  customerSearchResult: Array<ICustomer>;
  selectedCustomer: ICustomer;
  currentCustomer: any;
}

export default function Single() {
  const history = useHistory();
  const { stubId }: any = useParams();
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // States
  const [ticketStubStateBeforeChanges, setTicketStubStateBeforeChanges] = useState<ITicketStubState>(undefined);
  const [ticketStubLoaded, setTicketStubLoaded] = useState<boolean>(false);

  const [ticketStubState, setTicketStubState] = useState({
    id: null,
    customer_id: null,
    code: "",
    expiry_date_string: "",
    expiry_date: new Date(),
    last4: null,
    pin: null,
    redemption_product: "",
    source_url: "",
    description: "",
    status: "",
    selectedDate: null,
    status_id: null,
    showDateSelector: false,
    customer: null,
    redeemed_facility_id: null,
    redeemed_at: null,
    facility: null,
    title: "",
    subtitle: "",
    logo_image: null,
    included: "",
    ticket_description: "",
  });

  const [changeCustomerState, setChangeCustomerState] = useState<IChangeCustomerState>({
    showChangeCustomer: false,
    showNewCustomer: false,
    customerQuery: "",
    customerSearching: false,
    customerSearchResult: [],
    selectedCustomer: null,
    currentCustomer: null,
  });
  // End States

  // Use Effects
  useEffect(() => {
    void loadTicketStub();
  }, []);

  useEffect(() => {
    let mounted = true;
    let timeoutId: NodeJS.Timeout = null;
    if (mounted === true) {
      timeoutId = global.setTimeout(() => {
        void changePlayerSearch(mounted, changeCustomerState.customerQuery);
      }, 500);
    }
    return () => {
      mounted = false;
      clearTimeout(timeoutId);
      setChangeCustomerState(prevState => ({ ...prevState, playerSearchResult: [] }));
    };
  }, [changeCustomerState.customerQuery]);
  // End Use Effects
  // Functions
  async function loadTicketStub() {
    const ticketStubRes = await GetTicketStub({ id: Number(stubId), extended: true }, true);

    if (ticketStubRes.status !== StatusCode.OK) {
      return;
    }

    const ticketStub = ticketStubRes.data[0];
    let currentExpiryDate = new Date();
    let tempDate = "";

    if (ticketStub.expiry_date === null) {
      tempDate = new Date().toISOString().slice(0, 10);
    } else {
      // const dateArray = ticketStub.expiry_date.split("-");

      tempDate = moment(ticketStub.expiry_date as string).format();

      currentExpiryDate = new Date(tempDate);
    }

    let redeemedAtFacility: any = null;

    if (ticketStub.redeemed_facility_id) {
      const facilityRes = await GetFacility({ id: ticketStub.redeemed_facility_id }, true);

      redeemedAtFacility = facilityRes.data[0];
    }

    let customer: ICustomer = null;
    if (ticketStub.customer_id) {
      const currentCustomer = await GetCustomer({ id: ticketStub.customer_id }, true);
      customer = currentCustomer.data[0];
    }
    // const currentExpiryDate = new Date(ticketStub.expiry_date);

    ReactDOM.unstable_batchedUpdates(() => {
      setTicketStubState(prev => ({
        ...prev,
        id: Number(ticketStub.id),
        customer_id: ticketStub.customer_id,
        code: ticketStub.code,
        expiry_date: ticketStub.ticket.expiry_type === "none" ? null : currentExpiryDate,
        expiry_date_string: tempDate,
        last4: ticketStub.last4,
        pin: ticketStub.pin,
        redemption_product: ticketStub.redemption_product,
        source_url: ticketStub.source_url,
        description: ticketStub.description,
        status: String(ticketStub.status),
        selectedDate: currentExpiryDate,
        customer: customer,
        redeemed_facility_id: ticketStub.redeemed_facility_id,
        showDateSelector: ticketStub.ticket.expiry_type === "none" ? false : true,
        redeemed_at: ticketStub.redeemed_at,
        facility: redeemedAtFacility,
        included: ticketStub.ticket.included,
        logo_image: ticketStub.ticket.logo_image,
        title: ticketStub.ticket.title,
        subtitle: ticketStub.ticket.subtitle,
        ticket_description: ticketStub.ticket.description,
      }));
      if (ticketStubStateBeforeChanges) {
        setTicketStubStateBeforeChanges(prev => ({
          ...prev,
          id: Number(ticketStub.id),
          customer_id: ticketStub.customer_id,
          code: ticketStub.code,
          expiry_date: ticketStub.ticket.expiry_type === "none" ? null : currentExpiryDate,
          expiry_date_string: tempDate,
          last4: ticketStub.last4,
          pin: ticketStub.pin,
          redemption_product: ticketStub.redemption_product,
          source_url: ticketStub.source_url,
          description: ticketStub.description,
          status: String(ticketStub.status),
          selectedDate: currentExpiryDate,
          customer: customer,
          redeemed_facility_id: ticketStub.redeemed_facility_id,
          showDateSelector: ticketStub.ticket.expiry_type === "none" ? false : true,
          redeemed_at: ticketStub.redeemed_at,
          facility: redeemedAtFacility,
          included: ticketStub.ticket.included,
          logo_image: ticketStub.ticket.logo_image,
          title: ticketStub.ticket.title,
          subtitle: ticketStub.ticket.subtitle,
          ticket_description: ticketStub.ticket.description,
        }));
      }
      setTicketStubLoaded(true);
    });
  }

  async function saveTicketStub() {
    const res = await PutTicketStub(ticketStubState, true);

    if (res.status !== StatusCode.OK) {
      return;
    }

    const reloadUrl = "/admin/tickets/stubs/" + String(stubId);

    setTicketStubStateBeforeChanges(ticketStubState);

    history.push(reloadUrl);
  }

  function cancelUnsavedChanges() {
    setTicketStubState(ticketStubStateBeforeChanges);
  }

  function setExpiryDate(date: Date) {
    setTicketStubState(prevState => ({ ...prevState, expiry_date: date }));
  }

  async function createNewCustomer(customerInfo: ICustomerInfoState) {
    const inputError =
      customerInfo.firstName === "" ||
      customerInfo.lastName === "" ||
      (customerInfo.emailAddress === "" && customerInfo.phoneNumber === "");

    if (inputError) {
      //uiActions.showError(t("Please fill all fields"));
    }

    const customerRes = await PostCustomer(
      {
        first_name: customerInfo.firstName,
        last_name: customerInfo.lastName,
        phone: customerInfo.phoneNumber || null,
        email: customerInfo.emailAddress || null,
        state: "disabled",
      },
      true,
    );

    if (customerRes.status !== StatusCode.OK) {
      dispatch(showError(customerErrorMessage(t, customerRes?.data?.message as string)));
      return;
    }
    setChangeCustomerState(prevState => ({
      ...prevState,
      showNewCustomer: false,
      selectedCustomer: customerRes.data.data,
    }));
  }
  // End Functions
  // Handlers
  async function changePlayerSearch(mounted: boolean, customerSearchQuery: string) {
    try {
      if (customerSearchQuery === "") {
        if (mounted) {
          setChangeCustomerState(prevState => ({ ...prevState, customerSearchResult: [] }));
        }
        return;
      } else {
        setChangeCustomerState(prevState => ({ ...prevState, customerSearching: true }));
        const customerRes = await GetCustomer({ search: customerSearchQuery }, false);
        if (customerRes.status !== StatusCode.OK) {
          setChangeCustomerState(prevState => ({ ...prevState, customerSearching: false, customerSearchResult: [] }));
          return;
        } else if (mounted) {
          setChangeCustomerState(prevState => ({
            ...prevState,
            customerSearching: false,
            customerSearchResult: customerRes.data,
          }));
        }
      }
    } catch (error) {
      console.log("err", error);
    }
    return;
  }

  function unsavedChangesExist() {
    if (ticketStubStateBeforeChanges === undefined) {
      if (ticketStubLoaded) {
        setTicketStubStateBeforeChanges(ticketStubState);
      }
      return false;
    }

    return !isEqualWith(ticketStubStateBeforeChanges, ticketStubState, (originalValue, newValue) => {
      if ((isNull(originalValue) || originalValue === "") && (isNull(newValue) || newValue === "")) {
        return true;
      }
    });
  }

  function changePlayerHandleCustomerSearch(query: string) {
    setChangeCustomerState(prevState => ({ ...prevState, customerQuery: query }));
  }

  function changeCustomer() {
    //uiActions.showSuccess(orderCustomerRes.message);
    setTicketStubState(prevState => ({
      ...prevState,
      customer: changeCustomerState.selectedCustomer,
      customer_id: changeCustomerState.selectedCustomer.id,
    }));

    setChangeCustomerState(prevState => ({
      ...prevState,
      currentCustomer: null,
      showChangeCustomer: false,
      customerSearchResult: [],
      customerQuery: "",
      selectedCustomer: null,
    }));
  }

  function closeCreateNewCustomer() {
    setChangeCustomerState(prevState => ({
      ...prevState,
      showNewCustomer: false,
      first_name: "",
      last_name: "",
      phone: "",
      email: "",
    }));
  }

  function closeChangeCustomer() {
    closeCreateNewCustomer();
    setChangeCustomerState(prevState => ({
      ...prevState,
      showNewCustomer: false,
      currentCustomer: null,
      showChangeCustomer: false,
      customerQuery: "",
      customerSearchResult: [],
      selectedCustomer: null,
    }));
  }

  function handleChangePlayerSelection(id: number, customer: ICustomer) {
    setChangeCustomerState(prevState => ({ ...prevState, selectedCustomer: customer }));
  }

  function handleInputChange(event: any) {
    const { id, value } = event.target;
    setTicketStubState(prevState => ({ ...prevState, [id]: value }));
  }

  function handleShowCustomerChange(customer: any) {
    setChangeCustomerState(prevState => ({
      ...prevState,
      showChangeCustomer: true,
      showNewCustomer: false,
      customerQuery: "",
      customerSearchResult: [],
      currentCustomer: customer,
    }));
  }

  async function handleTicketDownload() {
    const ticketStubIds: any = [];
    ticketStubIds.push(ticketStubState.id);

    const params = {
      ticket_stub_ids: ticketStubIds,
    };

    const downloadReceiptRes = await GetTicketStubPdf(params, true);

    if (downloadReceiptRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.product.ticket_stub.026")));

      return;
    }

    window.open(downloadReceiptRes.data as string, "_blank");

    return;
  }

  async function handleTicketPrint() {
    const ticketStubIds: any = [];
    ticketStubIds.push(ticketStubState.id);

    const params = {
      ticket_stub_ids: ticketStubIds,
    };

    const downloadReceiptRes = await GetTicketStubReceipt(params, true);

    if (downloadReceiptRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.product.ticket_stub.026")));

      return;
    }

    window.open().document.write(downloadReceiptRes.data as string);

    return;
  }

  async function handleTicketVoid() {
    const putVoidStatus = await PutVoidStub({ id: ticketStubState.id }, true);

    if (putVoidStatus.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.product.ticket_stub.027")));
      return;
    }

    dispatch(showSuccess(t("secure.facility.product.ticket_stub.028")));
    void loadTicketStub();
  }

  async function handleRedeemStub(code: string) {
    const putStatusRes = await PutTicketStubRedeem({ code: code }, true);

    if (putStatusRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.product.ticket_stub.029")));
      return;
    }

    dispatch(showSuccess(t("secure.facility.product.ticket_stub.030")));
    void loadTicketStub();
  }

  async function handleUnredeemStub(stubID: number) {
    const putStatusRes = await PutUnredeemStub({ id: stubID }, true);

    if (putStatusRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.product.ticket_stub.031")));
      return;
    }

    dispatch(showSuccess(t("secure.facility.product.ticket_stub.032")));
    void loadTicketStub();
  }

  function handleTicketStub() {
    if (ticketStubState.status.toLowerCase() !== "void" || ticketStubState.status.toLowerCase() !== "expired") {
      if (ticketStubState.status.toLowerCase() === "valid") {
        void handleRedeemStub(ticketStubState.code);
      } else if (ticketStubState.status.toLowerCase() === "redeemed") {
        void handleUnredeemStub(ticketStubState.id as number);
      }
    } else {
      dispatch(showError(t("secure.facility.product.ticket_stub.033")));
      return;
    }
  }
  // End Handlers

  const primaryAction = {
    content:
      ticketStubState?.status?.toLowerCase() === "redeemed"
        ? t("secure.facility.product.ticket_stub.034")
        : t("secure.facility.product.ticket_stub.035"),
    action: () => handleTicketStub(),
    disabled:
      ticketStubState?.status?.toLowerCase() === "void" || ticketStubState?.status?.toLowerCase() === "expired"
        ? true
        : false,
  };

  return (
    ticketStubLoaded && (
      <Page
        title={ticketStubState?.code}
        narrow
        splitLayout
        primaryAction={primaryAction}
        breadcrumbs={[
          { prefix: true, label: t("secure.facility.product.ticket_stub.021"), url: "/admin/tickets/stubs" },
        ]}
        notificationBarProps={{
          isVisible: unsavedChangesExist(),
          onAction: saveTicketStub,
          onCancel: cancelUnsavedChanges,
        }}
        multipleActionDropdownAction={{
          label: t("secure.facility.product.ticket_stub.036"),
          dropdownProps: {
            alignment: "right",
            options: [
              {
                type: "handler",
                label: t("secure.facility.product.ticket_stub.037"),
                handler: () => handleTicketDownload(),
                icon: "download",
                // disabled: !productPermissions?.products_edit,
              },
              {
                type: "handler",
                label: t("secure.facility.product.ticket_stub.045"),
                handler: () => handleTicketPrint(),
                icon: "print",
                // disabled: !productPermissions?.products_edit,
              },
              {
                type: "handler",
                label: t("secure.facility.product.ticket_stub.038"),
                handler: () => handleTicketVoid(),
                icon: "ban",
                disabled: ticketStubState?.status?.toLowerCase() !== "valid" ? true : false,
              },
            ],
          },
        }}
      >
        <Page.Section twoThirds>
          <Card title={t("secure.facility.product.ticket_stub.007")}>
            <Card.Section>
              <FormLayout>
                <FormLayout.Group>
                  <div>
                    <Input
                      value={ticketStubState?.code}
                      label={t("secure.facility.product.ticket_stub.022")}
                      id="code"
                      onChange={handleInputChange}
                      placeholder={t("secure.facility.product.ticket_stub.022")}
                      readOnly
                    />
                  </div>

                  {ticketStubState?.showDateSelector ? (
                    <div id="ticket-date-selector" style={{ marginLeft: "15px" }}>
                      <DatePickerInput
                        label={t("secure.facility.product.ticket_stub.023")}
                        months={1}
                        position="left"
                        startingDate={ticketStubState?.selectedDate}
                        setStartingDate={setExpiryDate}
                      />
                    </div>
                  ) : null}
                </FormLayout.Group>

                <FormLayout.Group>
                  <Input
                    label={t("secure.facility.product.ticket_stub.020")}
                    id="status"
                    value={capitalize(ticketStubState?.status)}
                    disabled
                  />
                </FormLayout.Group>
                {ticketStubState?.redeemed_at && (
                  <FormLayout.Group>
                    <div>
                      <Input
                        value={ticketStubState?.facility?.long_name}
                        label={t("secure.facility.product.ticket_stub.024")}
                        id="redeemed_at"
                        onChange={handleInputChange}
                        readOnly
                      />
                    </div>
                    <div>
                      <Input
                        value={ticketStubState?.redeemed_at}
                        label={t("secure.facility.product.ticket_stub.025")}
                        id="redeemed_on"
                        onChange={handleInputChange}
                        readOnly
                      />
                    </div>
                  </FormLayout.Group>
                )}

                <FormLayout.Group>
                  <div>
                    <p>{t("secure.facility.product.ticket_stub.039")}</p>
                    <Markdown markdownText={ticketStubState?.description} />
                  </div>
                </FormLayout.Group>
              </FormLayout>
            </Card.Section>

            <Card.Section title={t("secure.facility.product.ticket_stub.044")}>
              <FormLayout>
                <FormLayout.Group>
                  <div className="ticket-details-container">
                    {ticketStubState?.logo_image ? (
                      <div>
                        <p>{t("secure.facility.product.ticket_stub.040")}</p>
                        <CustomerImage
                          imageSource={ticketStubState?.logo_image}
                          saveProfileImage={null}

                        // deleteProfileImage={deleteCustomerImage}
                        />
                      </div>
                    ) : null}

                    <p className="ticket-details-title">{ticketStubState?.title}</p>
                    <p className="ticket-details-subtitle">{ticketStubState?.subtitle}</p>
                    <p className="ticket-details-included">{ticketStubState?.included}</p>
                    <p className="ticket-details-description">{ticketStubState?.ticket_description}</p>
                  </div>
                </FormLayout.Group>
              </FormLayout>
            </Card.Section>
          </Card>
        </Page.Section>
        <Page.Section oneThird>
          <Card
            title={t("secure.facility.order.order.023")}
            titleActions={[
              {
                action: () => handleShowCustomerChange(ticketStubState?.customer),
                content: t("secure.facility.order.order.024"),
              },
            ]}
          >
            <Card.Section>
              <div className="order-customer">
                {ticketStubState?.customer ? (
                  <div>
                    <p className="text-lg">{ticketStubState.customer?.full_name}</p>
                    <p className="text-lg">{ticketStubState.customer?.email}</p>
                    <p className="text-lg">{ticketStubState.customer?.phone}</p>
                    <p className="text-lg">{ticketStubState.customer?.customer_type}</p>
                  </div>
                ) : (
                  <div>
                    <p>{t("secure.facility.order.order.025")}</p>
                  </div>
                )}
              </div>
            </Card.Section>
          </Card>
        </Page.Section>

        <ChangeCustomer
          title={t("secure.facility.product.ticket_stub.041")}
          currentCustomerTitle={t("secure.facility.product.ticket_stub.042")}
          newCustomerTitle={t("secure.facility.product.ticket_stub.043")}
          okText={t("secure.facility.order.order.028")}
          open={changeCustomerState.showChangeCustomer}
          changeCustomerState={changeCustomerState}
          searching={changeCustomerState.customerSearching}
          searchResults={changeCustomerState.customerSearchResult}
          newCustomerActive={changeCustomerState.showNewCustomer}
          onCancel={closeChangeCustomer}
          onOk={changeCustomer}
          handleSearchQueryChange={changePlayerHandleCustomerSearch}
          handleCustomerSelection={handleChangePlayerSelection}
          handleRemoveSelectedCustomer={() =>
            setChangeCustomerState(prevState => ({
              ...prevState,
              selectedCustomer: null,
              customerSearchResult: [],
              customerQuery: "",
            }))
          }
          handleCreateNewCustomer={createNewCustomer}
          handleCloseCreateNewCustomer={closeCreateNewCustomer}
          handleNewCustomerActive={() =>
            setChangeCustomerState(prevState => ({ ...prevState, showNewCustomer: true }))
          }
        />
      </Page>
    )
  );
}
